import { useEffect, useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { PageHeader } from "@/components/Bits";

const KINDS = [["webinar", "Live webinar / session"], ["new_asset", "New template or video"], ["news", "News"]];
const BLANK = { title: "", body: "", kind: "webinar", event_at: "", link: "", replay_url: "", expires_on: "" };

const Editor = ({ item, onClose, onSaved }) => {
  const [f, setF] = useState(item?.id ? { ...BLANK, ...item, event_at: item.event_at?.slice(0, 16) || "" } : BLANK);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    const body = { title: f.title, body: f.body, kind: f.kind, link: f.link.trim(), replay_url: (f.replay_url || "").trim(), expires_on: f.expires_on,
      event_at: f.event_at ? new Date(f.event_at).toISOString() : "" };
    try {
      const { data } = item?.id ? await api.put(`/admin/announcements/${item.id}`, body) : await api.post("/admin/announcements", body);
      toast.success("Announcement saved. Members see it on their dashboard.");
      onSaved(data);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const L = ({ children }) => <label className="text-xs font-mono uppercase text-muted-foreground">{children}</label>;
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent aria-describedby={undefined} className="max-w-xl bg-card border-border" data-testid="announcement-editor">
        <DialogHeader><DialogTitle className="font-serif text-2xl">{item?.id ? "Edit announcement" : "New announcement"}</DialogTitle></DialogHeader>
        <select value={f.kind} onChange={set("kind")} data-testid="announcement-kind" className="h-10 rounded-md border border-input bg-white px-3 text-sm">
          {KINDS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <Input value={f.title} onChange={set("title")} placeholder="e.g. Live Q&A: filling January" data-testid="announcement-title" className="bg-white" />
        <Textarea rows={3} value={f.body} onChange={set("body")} placeholder="One or two lines on why it's worth it" data-testid="announcement-body" className="bg-white" />
        {f.kind === "webinar" && <div className="space-y-1"><L>Date & time</L><Input type="datetime-local" value={f.event_at} onChange={set("event_at")} data-testid="announcement-event-at" className="bg-white" /></div>}
        <div className="space-y-1"><L>Link (https://, e.g. Zoom or booking page)</L><Input value={f.link} onChange={set("link")} placeholder="https://" data-testid="announcement-link" className="bg-white" /></div>
        {f.kind === "webinar" && (
          <div className="space-y-1"><L>Replay link (add after the session: YouTube, Vimeo or Loom)</L>
            <Input value={f.replay_url || ""} onChange={set("replay_url")} placeholder="https://" data-testid="announcement-replay-url" className="bg-white" />
            <p className="text-xs text-muted-foreground">Adding a replay puts it straight into the video library under "Webinar replays".</p>
          </div>
        )}
        <div className="space-y-1"><L>Hide after (optional)</L><Input type="date" value={f.expires_on} onChange={set("expires_on")} data-testid="announcement-expires" className="bg-white w-fit" /></div>
        <Button onClick={save} data-testid="announcement-save-btn" className="rounded-full bg-navy hover:bg-navy/90 text-white w-fit">Save</Button>
      </DialogContent>
    </Dialog>
  );
};

export default function AdminAnnouncements() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  useEffect(() => { api.get("/admin/announcements").then((r) => setItems(r.data)).catch(() => {}); }, []);
  const saved = (d) => { setItems((l) => (l.some((x) => x.id === d.id) ? l.map((x) => (x.id === d.id ? d : x)) : [d, ...l])); setEditing(null); };
  const remove = async (id) => {
    if (!window.confirm("Delete this announcement?")) return;
    await api.delete(`/admin/announcements/${id}`);
    setItems((l) => l.filter((x) => x.id !== id));
  };
  return (
    <div className="max-w-4xl">
      <PageHeader eyebrow="Announcements & live sessions" title="What's on this week.">
        <Button onClick={() => setEditing({})} data-testid="announcement-new-btn" className="rounded-full bg-navy hover:bg-navy/90 text-white"><Plus className="h-4 w-4 mr-2" />New announcement</Button>
      </PageHeader>
      <p className="text-sm text-muted-foreground mb-8 max-w-2xl">These appear in "This week in the club" on every member's dashboard, alongside new templates and personal nudges (ask a question, upload a campaign, the next seasonal pack).</p>
      <div className="rounded-2xl border border-border bg-card divide-y divide-border">
        {items.map((a) => (
          <div key={a.id} className="flex items-center gap-4 p-5" data-testid={`admin-announcement-${a.id}`}>
            <div className="min-w-0 flex-1">
              <div className="text-[10px] font-mono uppercase text-pinkb">{KINDS.find(([k]) => k === a.kind)?.[1]}{a.event_at && ` · ${new Date(a.event_at).toLocaleString("en-GB")}`}{a.expires_on && ` · until ${a.expires_on}`}{a.replay_url && " · replay in library"}</div>
              <div className="font-medium truncate">{a.title}</div>
            </div>
            <button onClick={() => setEditing(a)} data-testid={`announcement-edit-${a.id}`} className="p-2 text-muted-foreground hover:text-pinkb"><Pencil className="h-4 w-4" /></button>
            <button onClick={() => remove(a.id)} data-testid={`announcement-delete-${a.id}`} className="p-2 text-muted-foreground hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
        {items.length === 0 && <p className="p-6 text-sm text-muted-foreground">No announcements yet.</p>}
      </div>
      {editing && <Editor item={editing} onClose={() => setEditing(null)} onSaved={saved} />}
    </div>
  );
}
